'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { clampQuantity, MAX_QUANTITY, stepDown, stepUp } from '@/lib/quantity-rules';

interface QuantitySelectorProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** What − and + move by, and what a typed value is rounded up to: the pack size. */
  step?: number;
  max?: number;
  disabled?: boolean;
}

/**
 * The cart's quantity stepper, on the cart page and in the mini cart. Same rule arithmetic as the
 * product page's (lib/quantity-rules.ts); the line's minimum and pack size come from its
 * decoration, so a line of ammunition steps by the pack and stops at its minimum.
 */

export function QuantitySelector({
  value,
  onChange,
  min = 1,
  step = 1,
  max = MAX_QUANTITY,
  disabled = false
}: QuantitySelectorProps) {
  const rules = { min, step, max };
  const t = useTranslations('common');
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    // Below the minimum is treated like nonsense, not like a clamp. Clamping
    // turned a typed "0" into 1, which silently threw away a line of 5 — and
    // the customer's likely intent (remove the line) is already served by the
    // "Ta bort" link right beside this control. Reverting costs a keystroke;
    // clamping cost them their quantity. The same holds for a minimum order:
    // typing 500 on a line of 2000 cartridges with a 1000 minimum keeps 2000.
    if (Number.isNaN(parsed) || parsed < min) {
      setDraft(String(value));
      return;
    }
    // At or above the minimum: rounded UP to a whole pack, capped at the maximum.
    const clamped = clampQuantity(parsed, rules);
    onChange(clamped);
    setDraft(String(clamped));
  };

  // Deliberately more compact than the product page's stepper: this one sits
  // inside a cart row beside the price and the remove link, where the buy
  // box's 48px control would dominate the line. Same 3-4px radius and hairline
  // language, drawn at the canvas's cart metrics.
  const stepper =
    'flex h-8 w-9 items-center justify-center text-lg font-light leading-none text-foreground/60 ' +
    'transition-colors hover:bg-surface-subtle hover:text-foreground ' +
    'disabled:text-foreground/25 disabled:hover:bg-transparent';

  return (
    <div className="inline-flex items-center rounded-[3px] border border-foreground/20 transition-colors focus-within:border-accent-600">
      <button
        type="button"
        onClick={() => onChange(stepDown(value, rules))}
        disabled={disabled || value <= min}
        className={`${stepper} rounded-l-[3px]`}
        aria-label={t('decreaseQuantity')}
      >
        <span aria-hidden="true">−</span>
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            e.currentTarget.blur();
          }
        }}
        aria-label={t('quantity')}
        className="h-8 w-11 bg-transparent text-center text-[13.5px] font-semibold tabular-nums text-foreground outline-none disabled:opacity-50"
      />
      <button
        type="button"
        onClick={() => onChange(stepUp(value, rules))}
        disabled={disabled || value >= max}
        className={`${stepper} rounded-r-[3px]`}
        aria-label={t('increaseQuantity')}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
